import { useState } from 'react';
import { Panel } from './Card';
import { Button } from './Button';

interface SuggestionFormProps {
  /** A pre-filled context if the user is reporting on a specific game/card. */
  context?: { gameId?: string; cardId?: string };
  initialType?: 'bug' | 'card_idea';
  onClose?: () => void;
}

export function SuggestionForm({ context, initialType = 'bug', onClose }: SuggestionFormProps) {
  const [type, setType] = useState<'bug' | 'card_idea'>(initialType);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [ack, setAck] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (type === 'card_idea' && !ack) {
      setError('Please confirm you read the standards.');
      return;
    }
    setError(null);
    try {
      const r = await fetch('/api/suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, title, body, ...context }),
      });
      if (!r.ok) throw new Error('failed');
      setSubmitted(true);
    } catch {
      setError('Could not send. Try again in a moment.');
    }
  }

  if (submitted) {
    return (
      <Panel className="p-5">
        <h2 className="font-display text-xl font-bold">Thanks.</h2>
        <p className="text-sm text-ink-muted mt-1">It's in the queue. We read every one.</p>
        {onClose && (
          <Button className="mt-4" variant="secondary" onClick={onClose}>
            Close
          </Button>
        )}
      </Panel>
    );
  }

  return (
    <Panel className="p-5">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <h2 className="font-display text-xl font-bold">Send a suggestion</h2>
          <p className="text-sm text-ink-muted">Bug, idea, or a card you'd love to see.</p>
        </div>
        <div className="flex gap-2">
          {(['bug', 'card_idea'] as const).map((t) => (
            <button
              type="button"
              key={t}
              onClick={() => setType(t)}
              className={`h-9 px-4 rounded-md text-sm font-medium border transition focus-ring ${
                type === t ? 'border-accent bg-accent/5' : 'border-ink/10 text-ink-muted'
              }`}
            >
              {t === 'bug' ? 'Bug report' : 'Card idea'}
            </button>
          ))}
        </div>
        <label className="block">
          <div className="text-xs uppercase tracking-wider text-ink-muted mb-1">Title</div>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className="w-full h-10 px-3 rounded-md border border-ink/10 bg-surface focus-ring"
            placeholder={type === 'bug' ? 'What went wrong?' : 'Pawn: Hopping March'}
          />
        </label>
        <label className="block">
          <div className="text-xs uppercase tracking-wider text-ink-muted mb-1">Body</div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
            rows={6}
            className="w-full p-3 rounded-md border border-ink/10 bg-surface focus-ring resize-none"
            placeholder={
              type === 'bug'
                ? 'What happened, what you expected, and the game id if you have one.'
                : 'Describe the card in 1–2 sentences as a player would read it.'
            }
          />
        </label>
        {type === 'card_idea' && (
          <label className="flex items-start gap-2 text-sm text-ink-muted">
            <input
              type="checkbox"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
              className="mt-1"
            />
            <span>
              I read the{' '}
              <a href="/docs/card-design-standards" className="text-accent hover:underline">
                card design standards
              </a>{' '}
              and my idea follows them.
            </span>
          </label>
        )}
        {error && <div className="text-sm text-danger">{error}</div>}
        <div className="flex gap-2">
          <Button type="submit">Send</Button>
          {onClose && (
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          )}
        </div>
      </form>
    </Panel>
  );
}
